import React, { useEffect, useCallback, useState, useLayoutEffect } from 'react';
import PopperJS from 'popper.js';
import { Portal } from '../portal/Portal';
import { useDefaultRef } from '../utils/useDefaultRef';
import { Box } from '../grid/Grid';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

const defaultPopperOptions = {};

function autoSizing(data) {
  data.styles.width = data.offsets.reference.width;
  return data;
}

const autoSizeConfig = {
  enabled: true,
  fn: autoSizing,
  order: 840,
};

const placementMap = {
  bottom: 'bottom',
  bottomLeft: 'bottom-start',
  bottomRight: 'bottom-end',
  top: 'top',
  topLeft: 'top-start',
  topRight: 'top-end',
  right: 'right',
  left: 'left',
};

const TooltipWrap = styled(Box)`
  z-index: ${themeGet('zIndex.tooltip')};
`;

export const Popper = React.forwardRef(function Popper(props, ref) {
  const {
    referRef,
    children,
    container,
    keepMounted = false,
    disablePortal = false,
    disabledPopper = false,
    dynamicWidth = false,
    transition = false,
    open,
    placement: initialPlacement = 'bottom',
    popperOptions = defaultPopperOptions,
    popperRef: popperRefProp,
    ...other
  } = props;

  const [tooltipRef, setTooltipRef] = useDefaultRef(ref);
  const [popperRef, setPopperRef] = useDefaultRef(popperRefProp);
  const [placement, setPlacement] = useState(placementMap[initialPlacement] || placementMap.bottom);
  const [exited, setExited] = React.useState(true);

  const openPopper = useCallback(() => {
    if (disabledPopper || !tooltipRef.current || !referRef.current) {
      return;
    }

    if (popperRef.current) {
      popperRef.current.destroy();
      setPopperRef(null);
    }

    const handlePopperUpdate = data => {
      setPlacement(data.placement);
    };

    const popper = new PopperJS(referRef.current, tooltipRef.current, {
      placement: placementMap[initialPlacement],
      ...popperOptions,
      modifiers: {
        ...popperOptions.modifiers,
        ...(dynamicWidth ? { autoSizing: autoSizeConfig } : {}),
      },
      onCreate: (...args) => {
        handlePopperUpdate(...args);
        (popperOptions.onCreate || (() => {}))(...args);
      },
      onUpdate: (...args) => {
        handlePopperUpdate(...args);
        (popperOptions.onUpdate || (() => {}))(...args);
      },
    });
    setPopperRef(popper);
  }, [
    disabledPopper,
    tooltipRef,
    referRef,
    popperRef,
    initialPlacement,
    popperOptions,
    dynamicWidth,
    setPopperRef,
  ]);

  useEffect(() => {
    openPopper();
  }, [openPopper]);

  const handleClose = () => {
    if (!popperRef.current) {
      return;
    }

    popperRef.current.destroy();
    setPopperRef(null);
  };

  useEffect(() => {
    if (!open && !transition && !keepMounted) {
      // Otherwise handleExited will call this.
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transition, keepMounted]);

  useEffect(() => {
    return () => {
      handleClose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (popperRef.current) {
      popperRef.current.update();
    }
  });

  const handleRef = useCallback(
    node => {
      setTooltipRef(node);
      openPopper();
    },
    [setTooltipRef, openPopper]
  );

  const handleEnter = () => {
    if (exited) {
      setExited(false);
    }
  };

  const handleExited = () => {
    if (!exited) {
      setExited(true);
      handleClose();
    }
  };

  let childProps = { placement, open };

  if (transition) {
    childProps = {
      ...childProps,
      onEnter: handleEnter,
      onExit: handleExited,
    };
  }

  if (!keepMounted && !open && (!transition || exited)) {
    return null;
  }

  return (
    <Portal disablePortal={disablePortal} container={container}>
      <TooltipWrap
        ref={handleRef}
        role="tooltip"
        {...other}
        style={{
          // fix Popper.js display issue
          top: 0,
          left: 0,
        }}
      >
        {typeof children === 'function' ? children(childProps) : children}
      </TooltipWrap>
    </Portal>
  );
});

export default Popper;
